import { useToast } from "@chakra-ui/react";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiEmailById,
  apiMessageById,
  apiTaskById,
  resourceApiPathByPrefixId,
  v2ApiIssueActionsPathById,
  v2ApiNewHireActionsPathById,
} from "helpers/url/api/v2";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { emailKeys, setNewEmailInQueryClient, useUpdateEmail } from "features/Resources/Email/hooks";
import { titleize } from "helpers/string";
import { messageKeys, setNewMessageInQueryClient, useUpdateMessage } from "features/Resources/Message/hooks";
import { setNewTaskInQueryClient, taskKeys, useUpdateTask } from "features/Resources/Task/hooks";
import { issueKeys } from "features/Issues/hooks";
import { moveDateToNewDay } from "helpers/time";
import moment from "moment";
import { ActionV2, PrefixId, ActionSummary } from "./models";
import { fromActionPrefixId } from "./helpers";
import { meetingKeys, setNewMeetingInQueryClient, useUpdateMeeting } from "../Meeting/hooks";
import { checkpointKeys } from "../Checkpoint/hooks";
import { Task } from "../Task/models";
import { Message } from "../Message/models";
import { Meeting } from "../Meeting/models";
import { Email } from "../Email/models";

export const sharedResourceKeys = {
  all: ["sharedResources"] as const,
  lists: () => [...sharedResourceKeys.all, "list"] as const,
  list: (filters: string) => [...sharedResourceKeys.lists(), filters] as const,
  byNewHireJourneys: () => [...sharedResourceKeys.lists(), "newHireJourneys"] as const,
  byNewHireJourney: (id: string) => [...sharedResourceKeys.byNewHireJourneys(), id] as const,
  byIssues: () => [...sharedResourceKeys.lists(), "issues"] as const,
  byIssue: (id: string) => [...sharedResourceKeys.byIssues(), id] as const,
};

type KeySet = typeof emailKeys | typeof taskKeys | typeof messageKeys | typeof meetingKeys | typeof checkpointKeys;

const keyMap: Record<string, KeySet> = {
  email: emailKeys,
  message: messageKeys,
  task: taskKeys,
  meeting: meetingKeys,
  checkpoint: checkpointKeys,
};

export const getKeySetByPrefixId = (prefixId: PrefixId | undefined): KeySet | null => {
  const { resourceType } = fromActionPrefixId(prefixId);
  const keySet = keyMap[resourceType as unknown as string];
  if (!keySet) {
    console.error(`[getKeySetByPrefixId] No key set found for resourceType "${resourceType}" (prefixId: ${prefixId})`);
  }
  return keySet || null;
};

export const getDetailKeyByPrefixId = (prefixId: PrefixId | undefined): readonly string[] => {
  if (!prefixId) return ["no-op"];
  const { id } = fromActionPrefixId(prefixId);
  const keySet = getKeySetByPrefixId(prefixId);
  if (!keySet) {
    return ["error"];
  }
  return keySet?.detail(id as string);
};

export function insertSharedResourceInCacheByNewHireJourneyId(
  queryClient: QueryClient,
  newHireJourneyId: string,
  updatedResource: ActionSummary,
) {
  queryClient.setQueryData<ActionSummary[]>(sharedResourceKeys.byNewHireJourney(newHireJourneyId), (old) => {
    if (!old || old.length === 0) {
      return [updatedResource];
    }

    const exists = old.some((item) => item.prefixId === updatedResource.prefixId);

    if (exists) {
      return old.map((item) => (item.prefixId === updatedResource.prefixId ? updatedResource : item));
    }
    return [...old, updatedResource];
  });
}

export const actionDetailByPrefixQueryArgs = (prefixId: PrefixId) => ({
  queryKey: getDetailKeyByPrefixId(prefixId),
  queryFn: async () => apiClient.get<ActionV2 | null>(resourceApiPathByPrefixId(prefixId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
});

export const useActionDetailQueryByPrefixId = (prefixId: PrefixId | undefined, options: { enabled?: boolean } = {}) =>
  useQuery<ActionV2 | null>({
    ...actionDetailByPrefixQueryArgs(prefixId as PrefixId),
    enabled: !!prefixId && (options.enabled ?? true),
  });

export const useUpdateResourceByPrefixId = (prefixId: PrefixId | undefined) => {
  const { resourceType, id } = fromActionPrefixId(prefixId);
  const emailMutation = useUpdateEmail(id as string);
  const messageMutation = useUpdateMessage(id as string);
  const taskMutation = useUpdateTask(id);
  const meetingMutation = useUpdateMeeting(id as string);
  switch (resourceType) {
    case "email":
      return emailMutation;
    case "message":
      return messageMutation;
    case "task":
      return taskMutation;
    case "meeting":
      return meetingMutation;
    case "checkpoint":
    case undefined:
    default:
      return { mutate: () => {} };
  }
};

export function useForceSendByPrefixId(prefixId: PrefixId | undefined, onSuccess?: (res: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.post<ActionV2>(`${resourceApiPathByPrefixId(prefixId)}/force`, {}),
    onSuccess: (res) => {
      const { resourceType } = fromActionPrefixId(prefixId);
      toast({
        title: `${titleize(resourceType as unknown as string)} ${resourceType === "task" ? "notified" : "sent"}!`,
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: getDetailKeyByPrefixId(prefixId) });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.byActions() });
      queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourneys() });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.count() });
      onSuccess?.(res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useSkipByPrefixId(prefixId: PrefixId | undefined, onSuccess?: (resp: any) => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.post<ActionV2>(`${resourceApiPathByPrefixId(prefixId)}/skip`, {}),
    onSuccess: (res) => {
      const { resourceType } = fromActionPrefixId(prefixId);
      toast({
        title: `${titleize(resourceType as unknown as string)} ${res.status === "skipped" ? "skipped" : "unskipped"}`,
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: getDetailKeyByPrefixId(prefixId) });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.byActions() });
      queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourneys() });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.count() });
      onSuccess?.(res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useRemoveByPrefixId(prefixId: PrefixId | undefined, onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.delete<ActionV2>(resourceApiPathByPrefixId(prefixId)),
    onSuccess: () => {
      const { resourceType } = fromActionPrefixId(prefixId);
      toast({
        title: `${titleize(resourceType as unknown as string)} deleted`,
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: getDetailKeyByPrefixId(prefixId) });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.byActions() });
      onSuccess?.();
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export const useSharedResourcesListByNewHireIdQuery = (newHireJourneyId: string, options = {}) =>
  useQuery<ActionSummary[]>({
    queryKey: sharedResourceKeys.byNewHireJourney(newHireJourneyId),
    queryFn: async () => apiClient.get(v2ApiNewHireActionsPathById(newHireJourneyId)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: !!newHireJourneyId && ((options as { enabled?: boolean }).enabled ?? true),
  });

export const useSharedResourcesListByIssueIdQuery = (issueId: string, options = {}) =>
  useQuery<ActionSummary[]>({
    queryKey: sharedResourceKeys.byIssue(issueId),
    queryFn: async () => apiClient.get(v2ApiIssueActionsPathById(issueId)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: !!issueId && ((options as { enabled?: boolean }).enabled ?? true),
  });

type UpdateSharedResourceDateParams = {
  prefixId: PrefixId;
  newDate: string;
  timezone: string;
  action: ActionSummary;
};

type UseUpdateSharedResourceDateByPrefixIdOptions<V = UpdateSharedResourceDateParams> = {
  onSuccess?: (result: ActionV2, variables: V) => void;
};

export function useUpdateSharedResourceDateByPrefixId(options?: UseUpdateSharedResourceDateByPrefixIdOptions) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ prefixId, newDate, timezone, action }: UpdateSharedResourceDateParams) => {
      const { resourceType, id } = fromActionPrefixId(prefixId);
      const newDateWithTime = moveDateToNewDay(action.unifiedDate as Date, newDate, timezone);
      const newAction = { ...action, unifiedDate: newDateWithTime };
      if (action?.action?.newHireJourneyId) {
        insertSharedResourceInCacheByNewHireJourneyId(queryClient, action.action.newHireJourneyId, newAction);
      }

      switch (resourceType) {
        case "email":
          return apiClient.put<Email>(apiEmailById(id as string), { email: { deliverAt: newDateWithTime } });
        case "message":
          return apiClient.put<Message>(apiMessageById(id as string), { message: { deliverAt: newDateWithTime } });
        case "task":
          return apiClient.put<Task>(apiTaskById(id), { task: { startOn: newDate } });
        case "meeting": {
          return apiClient.post<Meeting>(`/api/meetings/${id}/schedule`, { scheduleAt: newDateWithTime });
        }
        case "checkpoint":
        case undefined:
        default:
          throw new Error(`Unknown resource type for prefixId: ${prefixId}`);
      }
    },

    onSuccess: (result, variables) => {
      const { resourceType } = fromActionPrefixId(variables.prefixId);

      switch (resourceType) {
        case "email":
          setNewEmailInQueryClient(queryClient, result as Email);
          break;
        case "message":
          setNewMessageInQueryClient(queryClient, result as Message);
          break;
        case "task":
          setNewTaskInQueryClient(queryClient, result as Task);
          break;
        case "meeting":
          setNewMeetingInQueryClient(queryClient, result as Meeting);
          break;
        case "checkpoint":
        case undefined:
        default:
          break;
      }

      if (options?.onSuccess) {
        options.onSuccess(result, variables);
      }
    },
    onError: (e, _variables) => {
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.byNewHireJourneys() });
    },
  });
}

type UpdateSharedResourceDateTimeParams = {
  prefixId: PrefixId;
  newDate: string | Date;
  action: ActionSummary;
  meetingDuration?: number;
};

export function useUpdateSharedResourceDateAndTimeByPrefixId(
  options?: UseUpdateSharedResourceDateByPrefixIdOptions<UpdateSharedResourceDateTimeParams>,
) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ prefixId, newDate, action, meetingDuration }: UpdateSharedResourceDateTimeParams) => {
      const { resourceType, id } = fromActionPrefixId(prefixId);
      const newMomentDate = moment(newDate);
      const newAction = { ...action, unifiedDate: newMomentDate.toDate() };
      if (action?.action?.newHireJourneyId) {
        insertSharedResourceInCacheByNewHireJourneyId(queryClient, action.action.newHireJourneyId, newAction);
      }

      switch (resourceType) {
        case "email":
          return apiClient.put<Email>(apiEmailById(id as string), { email: { deliverAt: newDate } });
        case "message":
          return apiClient.put<Message>(apiMessageById(id as string), { message: { deliverAt: newDate } });
        case "task":
          return apiClient.put<Task>(apiTaskById(id), { task: { startOn: newDate } });
        case "meeting": {
          return apiClient.post<Meeting>(`/api/meetings/${id}/schedule`, {
            scheduleAt: newDate,
            durationMinutes: meetingDuration,
          });
        }
        case "checkpoint":
        case undefined:
        default:
          throw new Error(`Unknown resource type for prefixId: ${prefixId}`);
      }
    },

    onSuccess: (result, variables) => {
      const { resourceType } = fromActionPrefixId(variables.prefixId);

      switch (resourceType) {
        case "email":
          setNewEmailInQueryClient(queryClient, result as Email);
          break;
        case "message":
          setNewMessageInQueryClient(queryClient, result as Message);
          break;
        case "task":
          setNewTaskInQueryClient(queryClient, result as Task);
          break;
        case "meeting":
          setNewMeetingInQueryClient(queryClient, result as Meeting);
          break;
        case "checkpoint":
        case undefined:
        default:
          break;
      }

      if (options?.onSuccess) {
        options.onSuccess(result, variables);
      }
    },
    onError: (e, _variables) => {
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
      queryClient.invalidateQueries({ queryKey: sharedResourceKeys.byNewHireJourneys() });
    },
  });
}
